import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { LookupService } from './lookup.service';
import { PortalConfigurationView } from '../models/PortalConfigurationView';
import { ConfigService } from './config.service';
import { ZipContentValidator } from '../shared/utils/zip-content-validator.util';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  // Mirrors the Aclarian portal allow-list (Aclarian.Workflow.Core.Services FileUpload.cs
  // and workflow-administration UtilityService.ALLOWED_FILE_EXTENSIONS) so the Vendor Portal
  // blocks the same file types and shows the same "Invalid File Type" message client-side.
  static readonly ALLOWED_FILE_EXTENSIONS: string[] = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.png',
    '.jpg',
    '.jpeg',
    '.jng',
    '.gif',
    '.bmp',
    '.tif',
    '.tiff',
    '.csv',
    '.txt',
    '.zip',
  ];

  static readonly EXTENSION_MIME_MAP: Record<string, string[]> = {
    '.pdf': ['application/pdf'],
    '.doc': ['application/msword'],
    '.docx': [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    '.xls': ['application/vnd.ms-excel'],
    '.xlsx': [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    '.png': ['image/png'],
    '.jpg': ['image/jpeg'],
    '.jpeg': ['image/jpeg'],
    '.jng': ['image/x-jng', 'image/jng'],
    '.gif': ['image/gif'],
    '.bmp': ['image/bmp'],
    '.tif': ['image/tiff'],
    '.tiff': ['image/tiff'],
    '.csv': ['text/csv', 'application/csv', 'text/plain'],
    '.txt': ['text/plain'],
    '.zip': [
      'application/zip',
      'application/x-zip-compressed',
      'application/octet-stream',
    ],
  };

  private portalConfigurationRequested = false;

  constructor(
    private lookupService: LookupService,
    private configService: ConfigService,
  ) {
    this.loadPortalConfiguration();
  }

  /**
   * Validates an uploaded file's type against the allowed extension/MIME allow-list,
   * matching the Aclarian portal's client-side check. The server-side validation in
   * FileUpload.cs remains the authoritative secondary check.
   */
  isFileTypeAllowed(file: File): boolean {
    const lastDotIndex = file.name.lastIndexOf('.');
    const ext =
      lastDotIndex >= 0 ? file.name.substring(lastDotIndex).toLowerCase() : '';
    if (UtilityService.ALLOWED_FILE_EXTENSIONS.indexOf(ext) === -1) {
      return false;
    }
    const mimeType = (file.type || '').toLowerCase();
    if (!mimeType || mimeType === 'application/octet-stream') {
      return true;
    }
    const allowedMimes = UtilityService.EXTENSION_MIME_MAP[ext] ?? [];
    return allowedMimes.length === 0 || allowedMimes.indexOf(mimeType) !== -1;
  }

  /**
   * Builds the "unsupported file type" message, matching the Aclarian portal wording.
   * Pair with the 'Invalid File Type' notification title.
   */
  getUnsupportedFileTypeMessage(fileName: string): string {
    const allowed = UtilityService.ALLOWED_FILE_EXTENSIONS.map((e) =>
      e.replace('.', '').toUpperCase(),
    ).join(', ');
    return `The file "${fileName}" has an unsupported file type. Allowed types: ${allowed}.`;
  }

  /**
   * Detects a corrupted or empty attachment. A 0-byte payload is the case the
   * browser can actually see: the picker reports size 0 both for a genuinely
   * empty file and for one whose contents could not be read (moved or deleted
   * after selection). Mirrors the Aclarian portal guard added by CSPB-111.
   * Pair with the 'Invalid File' notification title.
   */
  isFileEmptyOrCorrupted(file: File | null | undefined): boolean {
    return !file || file.size <= 0;
  }

  /**
   * Builds the corrupted-or-empty message, matching the Aclarian portal wording
   * from CSPB-111. Pair with the 'Invalid File' notification title.
   */
  getCorruptedOrEmptyFileMessage(): string {
    return 'This file appears to be corrupted or empty. Please try again.';
  }

  /** Comma-separated extension list for an <input type="file"> accept attribute. */
  get acceptedFileExtensions(): string {
    return UtilityService.ALLOWED_FILE_EXTENSIONS.join(',');
  }

  /**
   * Inspects a .zip for executable/script entries, matching the Aclarian portal's
   * Check Request Form behaviour. Returns the error message to display, or null when
   * the file is not a zip or is safe. Pair with the 'Invalid File Type' notification title.
   */
  async getRestrictedZipContentMessage(file: File): Promise<string | null> {
    if (!(file?.name ?? '').toLowerCase().endsWith('.zip')) {
      return null;
    }
    try {
      const result = await ZipContentValidator.inspect(file);
      if (!result.allowed) {
        const restrictedNames = result.restrictedEntries
          .map((entry) => entry.name)
          .join(', ');
        return (
          `The file "${file.name}" contains restricted file types that are not allowed: ${restrictedNames}. ` +
          'ZIP archives must not contain executable or script files.'
        );
      }
      return null;
    } catch {
      return null;
    }
  }

  isNullOrEmpty(myString: string): boolean {
    return myString == null || myString.trim() === '';
  }
  objectToQueryString(obj: any) {
    let str = [];
    for (let p in obj) {
      if (obj.hasOwnProperty(p)) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
      }
    }
    return str.join('&');
  }
  formatCurrency(currency: number) {
    try {
      let converted = Number(currency);
      if (converted || converted == 0) {
        let changedVal = converted;
        if (converted < 0) {
          changedVal *= -1;
        }
        let value = Number(changedVal.toFixed(2)).toLocaleString();
        let checkFinal = value.split('.');
        if (checkFinal.length > 1) {
          if (checkFinal[1].length == 1) {
            value += '0';
          }
        }
        if (converted >= 0) {
          return `$${value}`;
        } else {
          return `($${value})`;
        }
      }
    } catch (e) {}
    return '--';
  }
  formatCurrencyWithoutDollarSign(currency: number) {
    try {
      let converted = Number(currency);
      if (converted || converted == 0) {
        let changedVal = converted;
        if (converted < 0) {
          changedVal *= -1;
        }
        let value = Number(changedVal.toFixed(2)).toLocaleString();
        let checkFinal = value.split('.');
        if (checkFinal.length > 1) {
          if (checkFinal[1].length == 1) {
            value += '0';
          }
        }
        if (converted >= 0) {
          return `${value}`;
        } else {
          return `(${value})`;
        }
      }
    } catch (e) {}
    return '--';
  }
  getFormValidationErrors(
    form: FormGroup,
    formErrors: any = {},
    parentPath: string = '',
  ): { [key: string]: any } {
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      const currentPath = parentPath ? `${parentPath}.${key}` : key;

      if (control instanceof FormGroup) {
        // Recursively check nested form groups
        this.getFormValidationErrors(control, formErrors, currentPath);
      } else if (control instanceof FormArray) {
        // Handle FormArray
        const formArray = control as FormArray;
        formArray.controls.forEach((ctrl, index) => {
          if (ctrl instanceof FormGroup) {
            this.getFormValidationErrors(
              ctrl,
              formErrors,
              `${currentPath}[${index}]`,
            );
          } else {
            const arrayControlErrors = ctrl.errors;
            if (arrayControlErrors !== null) {
              formErrors[`${currentPath}[${index}]`] = arrayControlErrors;
            }
          }
        });
      } else if (control) {
        // Check for errors on this control
        const controlErrors = control.errors;
        if (controlErrors !== null) {
          formErrors[currentPath] = controlErrors;
        }
      }
    });

    return formErrors;
  }
  getErrorMessages(errors: { [key: string]: any }): { [key: string]: string } {
    const messages: { [key: string]: string } = {};

    Object.keys(errors).forEach((controlName) => {
      const errorObj = errors[controlName];
      let message = '';

      // Process the first error found (in order of priority)
      if (errorObj.required) {
        message = 'This field is required';
      } else if (errorObj.email) {
        message = 'Please enter a valid email address';
      } else if (errorObj.minlength) {
        message = `Minimum length is ${errorObj.minlength.requiredLength} characters`;
      } else if (errorObj.maxlength) {
        message = `Maximum length is ${errorObj.maxlength.requiredLength} characters`;
      } else if (errorObj.pattern) {
        message = 'Invalid format';
      } else if (errorObj.min) {
        message = `Minimum value is ${errorObj.min.min}`;
      } else if (errorObj.max) {
        message = `Maximum value is ${errorObj.max.max}`;
      } else if (errorObj.matDatepickerParse) {
        message = 'Please enter a valid date';
      } else if (errorObj.invalidDateRange) {
        message = 'Invalid date range';
      } else if (errorObj.passwordMismatch) {
        message = 'Passwords do not match';
      } else if (errorObj.notUnique) {
        message = 'This value is already in use';
      } else {
        // For any other validation error, get the first error key
        const firstErrorKey = Object.keys(errorObj)[0];
        message = `Invalid value (${firstErrorKey})`;
      }

      messages[controlName] = message;
    });

    return messages;
  }
  getErrorMessage(err: any): string {
    let errorMessage = '';
    try {
      let error = JSON.parse(err);
      if (typeof error === 'object') {
        errorMessage = '<ul>';
        for (const property in error) {
          if (error.hasOwnProperty('message')) {
            errorMessage = error['message'];
            let errorArray = errorMessage.split('-> ');
            if (Array.isArray(errorArray)) {
              errorMessage = '<ul>';
              errorArray.forEach((errorItem) => {
                errorMessage += '<li>' + errorItem + '</li>';
              });
            }
            break;
          } else {
            let errorArray = error[property];
            if (Array.isArray(errorArray)) {
              errorArray.forEach((errorItem) => {
                errorMessage += '<li>' + errorItem + '</li>';
              });
            }
          }
        }
        if (errorMessage.indexOf('<ul>') >= 0) {
          errorMessage += '</ul>';
        }
      }
    } catch {
      errorMessage = err.error?.message || err.error || err.status || err;
    }
    return errorMessage;
  }
  alphaNumericCompareForSorting(a: any, b: any) {
    a = a ?? '';
    b = b ?? '';
    const aHasLetters = /[a-zA-Z]/.test(a);
    const bHasLetters = /[a-zA-Z]/.test(b);

    if (aHasLetters && bHasLetters) {
      const aNumeric = parseFloat(a);
      const bNumeric = parseFloat(b);
      if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
        if (aNumeric !== bNumeric) {
          return aNumeric - bNumeric;
        }
      }

      return a.localeCompare(b, undefined, { sensitivity: 'case' });
    } else if (aHasLetters) {
      return 1;
    } else if (bHasLetters) {
      return -1;
    } else {
      const aNumeric = parseFloat(a);
      const bNumeric = parseFloat(b);
      if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
        return aNumeric - bNumeric;
      }

      return a.localeCompare(b, undefined, { sensitivity: 'case' });
    }
  }
  testId(id: string) {
    if (id == 'create' || id == 'view' || id == 'edit') {
      return null;
    }

    if (id && id.indexOf('?') >= 0) {
      id = id.split('?')[0];
    }

    return id;
  }
  onlyNumbers(event: any, form: FormGroup, fieldName: string) {
    const input = event.target;
    const value = input.value.replace(/[^0-9]/g, '');
    input.value = value;
    form.get(fieldName)?.setValue(value);
  }
  getUsState() {
    return [
      {
        name: 'Alabama',
        abbreviation: 'AL',
      },
      {
        name: 'Alaska',
        abbreviation: 'AK',
      },
      {
        name: 'American Samoa',
        abbreviation: 'AS',
      },
      {
        name: 'Arizona',
        abbreviation: 'AZ',
      },
      {
        name: 'Arkansas',
        abbreviation: 'AR',
      },
      {
        name: 'California',
        abbreviation: 'CA',
      },
      {
        name: 'Colorado',
        abbreviation: 'CO',
      },
      {
        name: 'Connecticut',
        abbreviation: 'CT',
      },
      {
        name: 'Delaware',
        abbreviation: 'DE',
      },
      {
        name: 'District Of Columbia',
        abbreviation: 'DC',
      },
      {
        name: 'Federated States Of Micronesia',
        abbreviation: 'FM',
      },
      {
        name: 'Florida',
        abbreviation: 'FL',
      },
      {
        name: 'Georgia',
        abbreviation: 'GA',
      },
      {
        name: 'Guam',
        abbreviation: 'GU',
      },
      {
        name: 'Hawaii',
        abbreviation: 'HI',
      },
      {
        name: 'Idaho',
        abbreviation: 'ID',
      },
      {
        name: 'Illinois',
        abbreviation: 'IL',
      },
      {
        name: 'Indiana',
        abbreviation: 'IN',
      },
      {
        name: 'Iowa',
        abbreviation: 'IA',
      },
      {
        name: 'Kansas',
        abbreviation: 'KS',
      },
      {
        name: 'Kentucky',
        abbreviation: 'KY',
      },
      {
        name: 'Louisiana',
        abbreviation: 'LA',
      },
      {
        name: 'Maine',
        abbreviation: 'ME',
      },
      {
        name: 'Marshall Islands',
        abbreviation: 'MH',
      },
      {
        name: 'Maryland',
        abbreviation: 'MD',
      },
      {
        name: 'Massachusetts',
        abbreviation: 'MA',
      },
      {
        name: 'Michigan',
        abbreviation: 'MI',
      },
      {
        name: 'Minnesota',
        abbreviation: 'MN',
      },
      {
        name: 'Mississippi',
        abbreviation: 'MS',
      },
      {
        name: 'Missouri',
        abbreviation: 'MO',
      },
      {
        name: 'Montana',
        abbreviation: 'MT',
      },
      {
        name: 'Nebraska',
        abbreviation: 'NE',
      },
      {
        name: 'Nevada',
        abbreviation: 'NV',
      },
      {
        name: 'New Hampshire',
        abbreviation: 'NH',
      },
      {
        name: 'New Jersey',
        abbreviation: 'NJ',
      },
      {
        name: 'New Mexico',
        abbreviation: 'NM',
      },
      {
        name: 'New York',
        abbreviation: 'NY',
      },
      {
        name: 'North Carolina',
        abbreviation: 'NC',
      },
      {
        name: 'North Dakota',
        abbreviation: 'ND',
      },
      {
        name: 'Northern Mariana Islands',
        abbreviation: 'MP',
      },
      {
        name: 'Ohio',
        abbreviation: 'OH',
      },
      {
        name: 'Oklahoma',
        abbreviation: 'OK',
      },
      {
        name: 'Oregon',
        abbreviation: 'OR',
      },
      {
        name: 'Palau',
        abbreviation: 'PW',
      },
      {
        name: 'Pennsylvania',
        abbreviation: 'PA',
      },
      {
        name: 'Puerto Rico',
        abbreviation: 'PR',
      },
      {
        name: 'Rhode Island',
        abbreviation: 'RI',
      },
      {
        name: 'South Carolina',
        abbreviation: 'SC',
      },
      {
        name: 'South Dakota',
        abbreviation: 'SD',
      },
      {
        name: 'Tennessee',
        abbreviation: 'TN',
      },
      {
        name: 'Texas',
        abbreviation: 'TX',
      },
      {
        name: 'Utah',
        abbreviation: 'UT',
      },
      {
        name: 'Vermont',
        abbreviation: 'VT',
      },
      {
        name: 'Virgin Islands',
        abbreviation: 'VI',
      },
      {
        name: 'Virginia',
        abbreviation: 'VA',
      },
      {
        name: 'Washington',
        abbreviation: 'WA',
      },
      {
        name: 'West Virginia',
        abbreviation: 'WV',
      },
      {
        name: 'Wisconsin',
        abbreviation: 'WI',
      },
      {
        name: 'Wyoming',
        abbreviation: 'WY',
      },
    ];
  }

  getCountries() {
    return [
      {
        name: 'Afghanistan',
        codeA2: 'AF',
        codeA3: 'AFG',
      },
      {
        name: 'Åland Islands',
        codeA2: 'AX',
        codeA3: 'ALA',
      },
      { name: 'Albania', codeA2: 'AL', codeA3: 'ALB' },
      {
        name: 'Algeria',
        codeA2: 'DZ',
        codeA3: 'DZA',
      },
      {
        name: 'American Samoa',
        codeA2: 'AS',
        codeA3: 'ASM',
      },
      {
        name: 'Andorra',
        codeA2: 'AD',
        codeA3: 'AND',
      },
      {
        name: 'Angola',
        codeA2: 'AO',
        codeA3: 'AGO',
      },
      {
        name: 'Anguilla',
        codeA2: 'AI',
        codeA3: 'AIA',
      },
      {
        name: 'Antarctica',
        codeA2: 'AQ',
        codeA3: 'ATA',
      },
      {
        name: 'Antigua and Barbuda',
        codeA2: 'AG',
        codeA3: 'ATG',
      },
      {
        name: 'Argentina',
        codeA2: 'AR',
        codeA3: 'ARG',
      },
      { name: 'Armenia', codeA2: 'AM', codeA3: 'ARM' },
      {
        name: 'Aruba',
        codeA2: 'AW',
        codeA3: 'ABW',
      },
      {
        name: 'Australia',
        codeA2: 'AU',
        codeA3: 'AUS',
      },
      { name: 'Austria', codeA2: 'AT', codeA3: 'AUT' },
      {
        name: 'Azerbaijan',
        codeA2: 'AZ',
        codeA3: 'AZE',
      },
      {
        name: 'Bahamas',
        codeA2: 'BS',
        codeA3: 'BHS',
      },
      {
        name: 'Bahrain',
        codeA2: 'BH',
        codeA3: 'BHR',
      },
      {
        name: 'Bangladesh',
        codeA2: 'BD',
        codeA3: 'BGD',
      },
      {
        name: 'Barbados',
        codeA2: 'BB',
        codeA3: 'BRB',
      },
      {
        name: 'Belarus',
        codeA2: 'BY',
        codeA3: 'BLR',
      },
      { name: 'Belgium', codeA2: 'BE', codeA3: 'BEL' },
      {
        name: 'Belize',
        codeA2: 'BZ',
        codeA3: 'BLZ',
      },
      {
        name: 'Benin',
        codeA2: 'BJ',
        codeA3: 'BEN',
      },
      {
        name: 'Bermuda',
        codeA2: 'BM',
        codeA3: 'BMU',
      },
      {
        name: 'Bhutan',
        codeA2: 'BT',
        codeA3: 'BTN',
      },
      { name: 'Bolivia', codeA2: 'BO', codeA3: 'BOL' },
      {
        name: 'Bonaire, Sint Eustatius and Saba',
        codeA2: 'BQ',
        codeA3: 'BES',
      },
      {
        name: 'Bosnia and Herzegovina',
        codeA2: 'BA',
        codeA3: 'BIH',
      },
      {
        name: 'Botswana',
        codeA2: 'BW',
        codeA3: 'BWA',
      },
      {
        name: 'Bouvet Island',
        codeA2: 'BV',
        codeA3: 'BVT',
      },
      {
        name: 'Brazil',
        codeA2: 'BR',
        codeA3: 'BRA',
      },
      {
        name: 'British Indian Ocean Territory',
        codeA2: 'IO',
        codeA3: 'IOT',
      },
      {
        name: 'Brunei Darussalam',
        codeA2: 'BN',
        codeA3: 'BRN',
      },
      {
        name: 'Bulgaria',
        codeA2: 'BG',
        codeA3: 'BGR',
      },
      {
        name: 'Burkina Faso',
        codeA2: 'BF',
        codeA3: 'BFA',
      },
      {
        name: 'Burundi',
        codeA2: 'BI',
        codeA3: 'BDI',
      },
      {
        name: 'Cambodia',
        codeA2: 'KH',
        codeA3: 'KHM',
      },
      {
        name: 'Cameroon',
        codeA2: 'CM',
        codeA3: 'CMR',
      },
      {
        name: 'Canada',
        codeA2: 'CA',
        codeA3: 'CAN',
      },
      {
        name: 'Cape Verde',
        codeA2: 'CV',
        codeA3: 'CPV',
      },
      {
        name: 'Cayman Islands',
        codeA2: 'KY',
        codeA3: 'CYM',
      },
      {
        name: 'Central African Republic',
        codeA2: 'CF',
        codeA3: 'CAF',
      },
      {
        name: 'Chad',
        codeA2: 'TD',
        codeA3: 'TCD',
      },
      {
        name: 'Chile',
        codeA2: 'CL',
        codeA3: 'CHL',
      },
      {
        name: 'China',
        codeA2: 'CN',
        codeA3: 'CHN',
      },
      {
        name: 'Christmas Island',
        codeA2: 'CX',
        codeA3: 'CXR',
      },
      {
        name: 'Cocos (Keeling) Islands',
        codeA2: 'CC',
        codeA3: 'CCK',
      },
      {
        name: 'Colombia',
        codeA2: 'CO',
        codeA3: 'COL',
      },
      { name: 'Comoros', codeA2: 'KM', codeA3: 'COM' },
      { name: 'Congo', codeA2: 'CG', codeA3: 'COG' },
      { name: 'Congo, Democratic Republic of the', codeA2: 'CD', codeA3: 'COD' },
      { name: 'Cook Islands', codeA2: 'CK', codeA3: 'COK' },
      { name: 'Costa Rica', codeA2: 'CR', codeA3: 'CRI' },
      { name: "Côte d'Ivoire", codeA2: 'CI', codeA3: 'CIV' },
      { name: 'Croatia', codeA2: 'HR', codeA3: 'HRV' },
      { name: 'Cuba', codeA2: 'CU', codeA3: 'CUB' },
      { name: 'Curaçao', codeA2: 'CW', codeA3: 'CUW' },
      { name: 'Cyprus', codeA2: 'CY', codeA3: 'CYP' },
      { name: 'Czech Republic', codeA2: 'CZ', codeA3: 'CZE' },
      { name: 'Denmark', codeA2: 'DK', codeA3: 'DNK' },
      { name: 'Djibouti', codeA2: 'DJ', codeA3: 'DJI' },
      { name: 'Dominica', codeA2: 'DM', codeA3: 'DMA' },
      { name: 'Dominican Republic', codeA2: 'DO', codeA3: 'DOM' },
      { name: 'Ecuador', codeA2: 'EC', codeA3: 'ECU' },
      { name: 'Egypt', codeA2: 'EG', codeA3: 'EGY' },
      { name: 'El Salvador', codeA2: 'SV', codeA3: 'SLV' },
      { name: 'Equatorial Guinea', codeA2: 'GQ', codeA3: 'GNQ' },
      { name: 'Eritrea', codeA2: 'ER', codeA3: 'ERI' },
      { name: 'Estonia', codeA2: 'EE', codeA3: 'EST' },
      { name: 'Eswatini', codeA2: 'SZ', codeA3: 'SWZ' },
      { name: 'Ethiopia', codeA2: 'ET', codeA3: 'ETH' },
      { name: 'Falkland Islands (Malvinas)', codeA2: 'FK', codeA3: 'FLK' },
      { name: 'Faroe Islands', codeA2: 'FO', codeA3: 'FRO' },
      { name: 'Fiji', codeA2: 'FJ', codeA3: 'FJI' },
      { name: 'Finland', codeA2: 'FI', codeA3: 'FIN' },
      {
        name: 'France',
        codeA2: 'FR',
        codeA3: 'FRA',
      },
      { name: 'French Guiana', codeA2: 'GF', codeA3: 'GUF' },
      { name: 'French Polynesia', codeA2: 'PF', codeA3: 'PYF' },
      { name: 'French Southern Territories', codeA2: 'TF', codeA3: 'ATF' },
      { name: 'Gabon', codeA2: 'GA', codeA3: 'GAB' },
      { name: 'Gambia', codeA2: 'GM', codeA3: 'GMB' },
      { name: 'Georgia', codeA2: 'GE', codeA3: 'GEO' },
      {
        name: 'Germany',
        codeA2: 'DE',
        codeA3: 'DEU',
      },
      { name: 'Ghana', codeA2: 'GH', codeA3: 'GHA' },
      { name: 'Gibraltar', codeA2: 'GI', codeA3: 'GIB' },
      { name: 'Greece', codeA2: 'GR', codeA3: 'GRC' },
      { name: 'Greenland', codeA2: 'GL', codeA3: 'GRL' },
      { name: 'Grenada', codeA2: 'GD', codeA3: 'GRD' },
      { name: 'Guadeloupe', codeA2: 'GP', codeA3: 'GLP' },
      { name: 'Guam', codeA2: 'GU', codeA3: 'GUM' },
      { name: 'Guatemala', codeA2: 'GT', codeA3: 'GTM' },
      { name: 'Guernsey', codeA2: 'GG', codeA3: 'GGY' },
      { name: 'Guinea', codeA2: 'GN', codeA3: 'GIN' },
      { name: 'Guinea-Bissau', codeA2: 'GW', codeA3: 'GNB' },
      { name: 'Guyana', codeA2: 'GY', codeA3: 'GUY' },
      { name: 'Haiti', codeA2: 'HT', codeA3: 'HTI' },
      { name: 'Heard Island and McDonald Islands', codeA2: 'HM', codeA3: 'HMD' },
      { name: 'Holy See (Vatican City State)', codeA2: 'VA', codeA3: 'VAT' },
      { name: 'Honduras', codeA2: 'HN', codeA3: 'HND' },
      { name: 'Hong Kong', codeA2: 'HK', codeA3: 'HKG' },
      { name: 'Hungary', codeA2: 'HU', codeA3: 'HUN' },
      { name: 'Iceland', codeA2: 'IS', codeA3: 'ISL' },
      {
        name: 'India',
        codeA2: 'IN',
        codeA3: 'IND',
      },
      { name: 'Indonesia', codeA2: 'ID', codeA3: 'IDN' },
      { name: 'Iran, Islamic Republic of', codeA2: 'IR', codeA3: 'IRN' },
      { name: 'Iraq', codeA2: 'IQ', codeA3: 'IRQ' },
      { name: 'Ireland', codeA2: 'IE', codeA3: 'IRL' },
      { name: 'Isle of Man', codeA2: 'IM', codeA3: 'IMN' },
      { name: 'Israel', codeA2: 'IL', codeA3: 'ISR' },
      { name: 'Italy', codeA2: 'IT', codeA3: 'ITA' },
      { name: 'Jamaica', codeA2: 'JM', codeA3: 'JAM' },
      { name: 'Japan', codeA2: 'JP', codeA3: 'JPN' },
      { name: 'Jersey', codeA2: 'JE', codeA3: 'JEY' },
      { name: 'Jordan', codeA2: 'JO', codeA3: 'JOR' },
      { name: 'Kazakhstan', codeA2: 'KZ', codeA3: 'KAZ' },
      { name: 'Kenya', codeA2: 'KE', codeA3: 'KEN' },
      { name: 'Kiribati', codeA2: 'KI', codeA3: 'KIR' },
      { name: "Korea, Democratic People's Republic of", codeA2: 'KP', codeA3: 'PRK' },
      { name: 'Korea, Republic of', codeA2: 'KR', codeA3: 'KOR' },
      { name: 'Kuwait', codeA2: 'KW', codeA3: 'KWT' },
      { name: 'Kyrgyzstan', codeA2: 'KG', codeA3: 'KGZ' },
      { name: "Lao People's Democratic Republic", codeA2: 'LA', codeA3: 'LAO' },
      { name: 'Latvia', codeA2: 'LV', codeA3: 'LVA' },
      { name: 'Lebanon', codeA2: 'LB', codeA3: 'LBN' },
      { name: 'Lesotho', codeA2: 'LS', codeA3: 'LSO' },
      { name: 'Liberia', codeA2: 'LR', codeA3: 'LBR' },
      { name: 'Libya', codeA2: 'LY', codeA3: 'LBY' },
      { name: 'Liechtenstein', codeA2: 'LI', codeA3: 'LIE' },
      { name: 'Lithuania', codeA2: 'LT', codeA3: 'LTU' },
      { name: 'Luxembourg', codeA2: 'LU', codeA3: 'LUX' },
      { name: 'Macao', codeA2: 'MO', codeA3: 'MAC' },
      { name: 'North Macedonia', codeA2: 'MK', codeA3: 'MKD' },
      { name: 'Madagascar', codeA2: 'MG', codeA3: 'MDG' },
      { name: 'Malawi', codeA2: 'MW', codeA3: 'MWI' },
      { name: 'Malaysia', codeA2: 'MY', codeA3: 'MYS' },
      { name: 'Maldives', codeA2: 'MV', codeA3: 'MDV' },
      { name: 'Mali', codeA2: 'ML', codeA3: 'MLI' },
      { name: 'Malta', codeA2: 'MT', codeA3: 'MLT' },
      { name: 'Marshall Islands', codeA2: 'MH', codeA3: 'MHL' },
      { name: 'Martinique', codeA2: 'MQ', codeA3: 'MTQ' },
      { name: 'Mauritania', codeA2: 'MR', codeA3: 'MRT' },
      { name: 'Mauritius', codeA2: 'MU', codeA3: 'MUS' },
      { name: 'Mayotte', codeA2: 'YT', codeA3: 'MYT' },
      {
        name: 'Mexico',
        codeA2: 'MX',
        codeA3: 'MEX',
      },
      { name: 'Micronesia, Federated States of', codeA2: 'FM', codeA3: 'FSM' },
      { name: 'Moldova, Republic of', codeA2: 'MD', codeA3: 'MDA' },
      { name: 'Monaco', codeA2: 'MC', codeA3: 'MCO' },
      { name: 'Mongolia', codeA2: 'MN', codeA3: 'MNG' },
      { name: 'Montenegro', codeA2: 'ME', codeA3: 'MNE' },
      { name: 'Montserrat', codeA2: 'MS', codeA3: 'MSR' },
      { name: 'Morocco', codeA2: 'MA', codeA3: 'MAR' },
      { name: 'Mozambique', codeA2: 'MZ', codeA3: 'MOZ' },
      { name: 'Myanmar', codeA2: 'MM', codeA3: 'MMR' },
      { name: 'Namibia', codeA2: 'NA', codeA3: 'NAM' },
      { name: 'Nauru', codeA2: 'NR', codeA3: 'NRU' },
      { name: 'Nepal', codeA2: 'NP', codeA3: 'NPL' },
      { name: 'Netherlands', codeA2: 'NL', codeA3: 'NLD' },
      { name: 'New Caledonia', codeA2: 'NC', codeA3: 'NCL' },
      { name: 'New Zealand', codeA2: 'NZ', codeA3: 'NZL' },
      { name: 'Nicaragua', codeA2: 'NI', codeA3: 'NIC' },
      { name: 'Niger', codeA2: 'NE', codeA3: 'NER' },
      { name: 'Nigeria', codeA2: 'NG', codeA3: 'NGA' },
      { name: 'Niue', codeA2: 'NU', codeA3: 'NIU' },
      { name: 'Norfolk Island', codeA2: 'NF', codeA3: 'NFK' },
      { name: 'Northern Mariana Islands', codeA2: 'MP', codeA3: 'MNP' },
      { name: 'Norway', codeA2: 'NO', codeA3: 'NOR' },
      { name: 'Oman', codeA2: 'OM', codeA3: 'OMN' },
      { name: 'Pakistan', codeA2: 'PK', codeA3: 'PAK' },
      { name: 'Palau', codeA2: 'PW', codeA3: 'PLW' },
      { name: 'Palestine, State of', codeA2: 'PS', codeA3: 'PSE' },
      { name: 'Panama', codeA2: 'PA', codeA3: 'PAN' },
      { name: 'Papua New Guinea', codeA2: 'PG', codeA3: 'PNG' },
      { name: 'Paraguay', codeA2: 'PY', codeA3: 'PRY' },
      { name: 'Peru', codeA2: 'PE', codeA3: 'PER' },
      { name: 'Philippines', codeA2: 'PH', codeA3: 'PHL' },
      { name: 'Pitcairn', codeA2: 'PN', codeA3: 'PCN' },
      { name: 'Poland', codeA2: 'PL', codeA3: 'POL' },
      { name: 'Portugal', codeA2: 'PT', codeA3: 'PRT' },
      { name: 'Puerto Rico', codeA2: 'PR', codeA3: 'PRI' },
      { name: 'Qatar', codeA2: 'QA', codeA3: 'QAT' },
      { name: 'Réunion', codeA2: 'RE', codeA3: 'REU' },
      { name: 'Romania', codeA2: 'RO', codeA3: 'ROU' },
      { name: 'Russian Federation', codeA2: 'RU', codeA3: 'RUS' },
      { name: 'Rwanda', codeA2: 'RW', codeA3: 'RWA' },
      { name: 'Saint Barthélemy', codeA2: 'BL', codeA3: 'BLM' },
      { name: 'Saint Helena, Ascension and Tristan da Cunha', codeA2: 'SH', codeA3: 'SHN' },
      { name: 'Saint Kitts and Nevis', codeA2: 'KN', codeA3: 'KNA' },
      { name: 'Saint Lucia', codeA2: 'LC', codeA3: 'LCA' },
      { name: 'Saint Martin (French part)', codeA2: 'MF', codeA3: 'MAF' },
      { name: 'Saint Pierre and Miquelon', codeA2: 'PM', codeA3: 'SPM' },
      { name: 'Saint Vincent and the Grenadines', codeA2: 'VC', codeA3: 'VCT' },
      { name: 'Samoa', codeA2: 'WS', codeA3: 'WSM' },
      { name: 'San Marino', codeA2: 'SM', codeA3: 'SMR' },
      { name: 'Sao Tome and Principe', codeA2: 'ST', codeA3: 'STP' },
      { name: 'Saudi Arabia', codeA2: 'SA', codeA3: 'SAU' },
      { name: 'Senegal', codeA2: 'SN', codeA3: 'SEN' },
      { name: 'Serbia', codeA2: 'RS', codeA3: 'SRB' },
      { name: 'Seychelles', codeA2: 'SC', codeA3: 'SYC' },
      { name: 'Sierra Leone', codeA2: 'SL', codeA3: 'SLE' },
      { name: 'Singapore', codeA2: 'SG', codeA3: 'SGP' },
      { name: 'Sint Maarten (Dutch part)', codeA2: 'SX', codeA3: 'SXM' },
      { name: 'Slovakia', codeA2: 'SK', codeA3: 'SVK' },
      { name: 'Slovenia', codeA2: 'SI', codeA3: 'SVN' },
      { name: 'Solomon Islands', codeA2: 'SB', codeA3: 'SLB' },
      { name: 'Somalia', codeA2: 'SO', codeA3: 'SOM' },
      { name: 'South Africa', codeA2: 'ZA', codeA3: 'ZAF' },
      { name: 'South Georgia and the South Sandwich Islands', codeA2: 'GS', codeA3: 'SGS' },
      { name: 'South Sudan', codeA2: 'SS', codeA3: 'SSD' },
      { name: 'Spain', codeA2: 'ES', codeA3: 'ESP' },
      { name: 'Sri Lanka', codeA2: 'LK', codeA3: 'LKA' },
      { name: 'Sudan', codeA2: 'SD', codeA3: 'SDN' },
      { name: 'Suriname', codeA2: 'SR', codeA3: 'SUR' },
      { name: 'Svalbard and Jan Mayen', codeA2: 'SJ', codeA3: 'SJM' },
      { name: 'Sweden', codeA2: 'SE', codeA3: 'SWE' },
      { name: 'Switzerland', codeA2: 'CH', codeA3: 'CHE' },
      { name: 'Syrian Arab Republic', codeA2: 'SY', codeA3: 'SYR' },
      { name: 'Taiwan', codeA2: 'TW', codeA3: 'TWN' },
      { name: 'Tajikistan', codeA2: 'TJ', codeA3: 'TJK' },
      { name: 'Tanzania, United Republic of', codeA2: 'TZ', codeA3: 'TZA' },
      { name: 'Thailand', codeA2: 'TH', codeA3: 'THA' },
      { name: 'Timor-Leste', codeA2: 'TL', codeA3: 'TLS' },
      { name: 'Togo', codeA2: 'TG', codeA3: 'TGO' },
      { name: 'Tokelau', codeA2: 'TK', codeA3: 'TKL' },
      { name: 'Tonga', codeA2: 'TO', codeA3: 'TON' },
      { name: 'Trinidad and Tobago', codeA2: 'TT', codeA3: 'TTO' },
      { name: 'Tunisia', codeA2: 'TN', codeA3: 'TUN' },
      { name: 'Turkey', codeA2: 'TR', codeA3: 'TUR' },
      { name: 'Turkmenistan', codeA2: 'TM', codeA3: 'TKM' },
      { name: 'Turks and Caicos Islands', codeA2: 'TC', codeA3: 'TCA' },
      { name: 'Tuvalu', codeA2: 'TV', codeA3: 'TUV' },
      { name: 'Uganda', codeA2: 'UG', codeA3: 'UGA' },
      { name: 'Ukraine', codeA2: 'UA', codeA3: 'UKR' },
      { name: 'United Arab Emirates', codeA2: 'AE', codeA3: 'ARE' },
      {
        name: 'United Kingdom',
        codeA2: 'GB',
        codeA3: 'GBR',
      },
      {
        name: 'United States',
        codeA2: 'US',
        codeA3: 'USA',
      },
      { name: 'United States Minor Outlying Islands', codeA2: 'UM', codeA3: 'UMI' },
      { name: 'Uruguay', codeA2: 'UY', codeA3: 'URY' },
      { name: 'Uzbekistan', codeA2: 'UZ', codeA3: 'UZB' },
      { name: 'Vanuatu', codeA2: 'VU', codeA3: 'VUT' },
      { name: 'Venezuela', codeA2: 'VE', codeA3: 'VEN' },
      { name: 'Viet Nam', codeA2: 'VN', codeA3: 'VNM' },
      { name: 'Virgin Islands, British', codeA2: 'VG', codeA3: 'VGB' },
      { name: 'Virgin Islands, U.S.', codeA2: 'VI', codeA3: 'VIR' },
      { name: 'Wallis and Futuna', codeA2: 'WF', codeA3: 'WLF' },
      { name: 'Western Sahara', codeA2: 'EH', codeA3: 'ESH' },
      { name: 'Yemen', codeA2: 'YE', codeA3: 'YEM' },
      { name: 'Zambia', codeA2: 'ZM', codeA3: 'ZMB' },
      { name: 'Zimbabwe', codeA2: 'ZW', codeA3: 'ZWE' },
    ];
  }

  ensurePortalConfigurationRequested(): void {
    if (!this.portalConfigurationRequested) {
      this.loadPortalConfiguration();
    }
  }

  private loadPortalConfiguration(): void {
    this.portalConfigurationRequested = true;
    this.lookupService.getPortalConfiguration().subscribe({
      next: (config) => {
        this.configService.setConfig(config);
        this.applyTheme(config);
      },
      error: (error) => {
        console.error('Failed to load portal configuration:', error);
        const defaultConfig = new PortalConfigurationView();
        this.configService.setConfig(defaultConfig);
      },
    });
  }

  private applyTheme(config: PortalConfigurationView): void {
    if (!config) return;
    const root = document.documentElement;
    if (config.portalPrimaryColor) {
      root.style.setProperty(
        '--tg-theme-secondary',
        config.portalPrimaryColor,
      );
    }
    if (config.portalSecondaryColor) {
      root.style.setProperty('--tg-theme-primary', config.portalSecondaryColor);
    }
  }

  public refreshPortalConfiguration(): void {
    this.loadPortalConfiguration();
  }

  public get portalConfig(): PortalConfigurationView {
    return this.configService.getConfig();
  }

  /**
   * Client name for portal copy. Configured portalDisplayName wins; until that
   * field exists in the portal configuration, falls back to the client name each
   * portal branch carries in environment.appName ("<Client Name> | VendorV2").
   */
  public get clientDisplayName(): string {
    const configured = this.portalConfig?.portalDisplayName;
    if (configured && configured.trim()) {
      return configured.trim();
    }
    const fromAppName = (environment.appName || '')
      .split('|')[0]
      .replace(/[\s\-–]*\b(UAT|TEST|QA|PREPROD)\b\s*$/i, '')
      .trim();
    return fromAppName || 'Sample City';
  }
}
